export type TransactionStatus = 'planned' | 'completed' | 'cancelled'
export type TransactionKind = 'income' | 'expense'

export interface Transaction {
  expenseClass?: 'fixed' | 'daily'
  movementPurpose?: 'ordinary' | 'savings' | 'card_payment'
  paymentMethod?: 'account' | 'credit_card'
  creditCardId?: string | null
  transferId?: string | null
  notes?: string | null
  id: string
  description: string
  category: string
  account: string
  date: string
  amount: number
  kind: TransactionKind
  status: TransactionStatus
  origin?: 'seed' | 'user' | 'remote'
  transactionDate?: string
  accountId?: string
  categoryId?: string | null
}

export interface ProjectionPoint {
  label: string
  balance: number
  projected?: boolean
}

interface RecurrenceLike {
  amount: string | number
  kind: TransactionKind
  frequency: 'monthly' | 'yearly'
  nextOccurrence: string
  active: boolean
}

function localDate(value: string) {
  return new Date(`${value.slice(0, 10)}T12:00:00`)
}

function apply(balance: number, amount: number, kind: TransactionKind) {
  return balance + (kind === 'income' ? amount : -amount)
}

export const financialEngine = {
  availableBalance(accounts: { balance: number }[]) {
    return accounts.reduce((sum, account) => sum + account.balance, 0)
  },
  monthlyTotals(transactions: Transaction[]) {
    return transactions.reduce(
      (totals, item) => {
        if (item.status === 'cancelled' || item.transferId) return totals
        totals[item.kind] += item.amount
        if (item.status === 'planned') totals.planned += item.amount
        return totals
      },
      { income: 0, expense: 0, planned: 0 },
    )
  },
  dailyBudget(monthlyBudget: number, daysInMonth: number) {
    return monthlyBudget / daysInMonth
  },
  savingsRate(income: number, expenses: number) {
    return income ? ((income - expenses) / income) * 100 : 0
  },
  projectedBalance(startBalance: number, transactions: Transaction[]) {
    return transactions.reduce((balance, item) => {
      if (item.status === 'cancelled' || item.transferId) return balance
      return balance + (item.kind === 'income' ? item.amount : -item.amount)
    }, startBalance)
  },
  projectionSeries({ startBalance, transactions, recurrences, monthlyBudget, cadence }: {
    startBalance: number
    transactions: Transaction[]
    recurrences: RecurrenceLike[]
    monthlyBudget: number
    cadence: 'daily' | 'monthly' | 'annual'
  }): ProjectionPoint[] {
    const start = localDate('2026-09-14')
    const planned = transactions.filter(item => !item.transferId && item.status === 'planned' && item.transactionDate)
    let balance = startBalance

    if (cadence === 'daily') {
      const dailyBudget = monthlyBudget / 30
      return Array.from({ length: 15 }, (_, index) => {
        const date = new Date(start); date.setDate(start.getDate() + index)
        const key = date.toISOString().slice(0, 10)
        for (const item of planned.filter(tx => tx.transactionDate?.slice(0, 10) === key)) balance = apply(balance, item.amount, item.kind)
        balance -= dailyBudget
        return { label: index === 0 ? 'Hoje' : new Intl.DateTimeFormat('pt-BR', { day:'2-digit', month:'short' }).format(date), balance, projected: true }
      })
    }

    const monthCount = cadence === 'monthly' ? 6 : 48
    const monthly = Array.from({ length: monthCount }, (_, index) => {
      const date = new Date(start.getFullYear(), start.getMonth() + index, 1, 12)
      const year = date.getFullYear(), month = date.getMonth()
      for (const item of planned) {
        const event = localDate(item.transactionDate!)
        if (event.getFullYear() === year && event.getMonth() === month) balance = apply(balance, item.amount, item.kind)
      }
      for (const recurrence of recurrences.filter(item => item.active)) {
        const first = localDate(recurrence.nextOccurrence)
        const eligible = recurrence.frequency === 'monthly'
          ? date >= new Date(first.getFullYear(), first.getMonth(), 1, 12)
          : month === first.getMonth() && year >= first.getFullYear()
        if (eligible) balance = apply(balance, Number(recurrence.amount), recurrence.kind)
      }
      balance -= index === 0 ? monthlyBudget * (17 / 30) : monthlyBudget
      return { date, balance }
    })

    if (cadence === 'monthly') return monthly.map(({ date, balance }, index) => ({ label: index === 0 ? 'Set' : new Intl.DateTimeFormat('pt-BR', { month:'short' }).format(date), balance, projected:true }))
    return [2026, 2027, 2028, 2029].map(year => ({ label:String(year), balance:monthly.filter(item => item.date.getFullYear() === year).at(-1)?.balance ?? balance, projected:true }))
  },
}

/** Realized cash performance. Credit purchases are counted when their bill is paid. */
export function monthlyPerformance(transactions:Transaction[], month:string, today:string){
  const totals={income:0,fixed:0,daily:0,expense:0,result:0}
  for(const t of transactions){
    if(t.transferId||t.status!=='completed'||!t.transactionDate?.startsWith(month)||t.transactionDate.slice(0,10)>today||t.paymentMethod==='credit_card')continue
    const amount=Math.round(t.amount*100)
    if(t.kind==='income')totals.income+=amount
    else {totals.expense+=amount;totals[t.expenseClass==='daily'?'daily':'fixed']+=amount}
  }
  totals.result=totals.income-totals.expense
  return Object.fromEntries(Object.entries(totals).map(([k,v])=>[k,v/100])) as typeof totals
}
